class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    // Reset infinite scroll on filter change
    InfiniteScrollHelper.reset();
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document.getElementById('ProductGridContainer').querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


// this is custom code for filte infinite loader

(function () {
  let loader = document.getElementById("loaders");
  let productGrid = document.getElementById("product-grid");
  let paginationContainer = document.getElementById("pagination");

  let currentPage = 1;
  let totalPages = getTotalPages();
  let loading = false;

  function getTotalPages() {
    if (!paginationContainer) return 1;

    let lastPageLink = paginationContainer.querySelector("a[href*='page=']:last-of-type");
    if (lastPageLink) {
      let url = new URL(lastPageLink.href);
      return parseInt(url.searchParams.get("page")) || 1;
    }

    return 1;
  }

  function buildNextPageURL() {
    const sectionId = productGrid.dataset.id; // Shopify section ID
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", currentPage + 1);
    return `${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`;
  }

  // function loadNextPage() {
  //   if (loading || currentPage >= totalPages) return;
  //   loading = true;
  //   loader.style.display = "block";

  //   fetch(buildNextPageURL())
  //     .then(res => res.text())
  //     .then(html => {
  //       const parser = new DOMParser();
  //       const doc = parser.parseFromString(html, "text/html");

  //       let newProducts = doc.querySelector("#product-grid");
  //       let newPagination = doc.querySelector("#pagination");

  //       if (newProducts) {
  //         productGrid.insertAdjacentHTML("beforeend", newProducts.innerHTML);
  //       }

  //       if (newPagination) {
  //         paginationContainer.innerHTML = newPagination.innerHTML;
  //       }

  //       // Update references
  //       paginationContainer = document.getElementById("pagination");
  //       totalPages = getTotalPages();

  //       currentPage++;
  //       loading = false;
  //       loader.style.display = "none";
  //     })
  //     .catch(() => {
  //       loading = false;
  //       loader.style.display = "none";
  //     });
  // }


  function loadNextPage() {
  if (loading || currentPage >= totalPages) return;
  loading = true;
  loader.style.display = "block";

  fetch(buildNextPageURL())
    .then(res => res.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      let newProducts = doc.querySelector("#product-grid");
      let newPagination = doc.querySelector("#pagination");

      if (newProducts) {
        productGrid.insertAdjacentHTML("beforeend", newProducts.innerHTML);

        // Preload images
        const imgs = productGrid.querySelectorAll("img");
        imgs.forEach(img => {
          if (!img.complete) {
            const src = img.dataset.src || img.src;
            const preImg = new Image();
            preImg.src = src;
          }
        });
      }

      if (newPagination) {
        paginationContainer.innerHTML = newPagination.innerHTML;
      }

      // Update references
      paginationContainer = document.getElementById("pagination");
      totalPages = getTotalPages();

      currentPage++;
      loading = false;
      loader.style.display = "none";
    })
    .catch(() => {
      loading = false;
      loader.style.display = "none";
    });
}


  // function onScroll() {
  
  //   console.log("document.documentElement.scrollHeight",document.documentElement.scrollHeight);
  //   console.log("window.scrollY + window.innerHeight",window.scrollY + window.innerHeight);

  //   if (loading) return;
  //   if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 100) {
  //     console.log("inner page", window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 100);
  //     loadNextPage();
  //   }
  // }


  function onScroll() {
  if (loading) return;

  const scrollPosition = window.scrollY + window.innerHeight;
  const pageHeight = document.documentElement.scrollHeight;
  loadNextPage();

  // Trigger load when user is 500px from bottom
  // if (scrollPosition >= pageHeight - 500) {
  //   loadNextPage();
  // }
}

  function initInfiniteLoader() {
    currentPage = 1;
    totalPages = getTotalPages();
    loading = false;
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll);
  }

  // Handle In Stock checkbox


const inStockCheckbox = document.getElementById("Filter-filter.v.availability-1");
const outOfStockCheckbox = document.getElementById("Filter-filter.v.availability-2");
const priceMinInput = document.getElementById("Filter-Price-GTE");
const priceMaxInput = document.getElementById("Filter-Price-LTE");
const sortSelect = document.getElementById("SortBy");

function applyFilters() {
  const searchParams = new URLSearchParams();

  // Reset page
  searchParams.set("page", "1");

  // Availability (can have multiple values)
  if (inStockCheckbox?.checked) {
    searchParams.append("filter.v.availability", "1");
  }
  if (outOfStockCheckbox?.checked) {
    searchParams.append("filter.v.availability", "0");
  }

  // Price range (only if both set)
  if (priceMinInput?.value && priceMaxInput?.value) {
    searchParams.set("filter.v.price.gte", priceMinInput.value);
    searchParams.set("filter.v.price.lte", priceMaxInput.value);
  }

  // Sort (if selected)
  if (sortSelect?.value) {
    searchParams.set("sort_by", sortSelect.value);
  }

  const sectionId = productGrid.dataset.id;
  loader.style.display = "block";

  fetch(`${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`)
    .then(res => res.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      let newProducts = doc.querySelector("#product-grid");
      let newPagination = doc.querySelector("#pagination");

      if (newProducts) productGrid.innerHTML = newProducts.innerHTML;
      if (newPagination) paginationContainer.innerHTML = newPagination.innerHTML;

      // Update DOM references after replacing HTML
      productGrid = document.getElementById("product-grid");
      paginationContainer = document.getElementById("pagination");
      totalPages = getTotalPages();

      // Update browser URL
      window.location.href = `${window.location.pathname}?${searchParams.toString()}`;

      initInfiniteLoader();
      loader.style.display = "none";
    })
    .catch(() => loader.style.display = "none");
}

// Events
[inStockCheckbox, outOfStockCheckbox].forEach(cb =>
  cb?.addEventListener("change", applyFilters)
);

[priceMinInput, priceMaxInput].forEach(inp =>
  inp?.addEventListener("change", () => {
    if (priceMinInput?.value && priceMaxInput?.value) {
      applyFilters();
    }
  })
);

sortSelect?.addEventListener("change", applyFilters);



  // Shopify section reload hook
  document.addEventListener("shopify:section:load", () => {
    productGrid = document.getElementById("product-grid");
    paginationContainer = document.getElementById("pagination");
    initInfiniteLoader();
  });

  initInfiniteLoader();
})();



function buildNextPageURL() {
  const sectionId = productGrid.dataset.id; // Shopify section ID
  const searchParams = new URLSearchParams(window.location.search); // always take from live URL
  searchParams.set("page", currentPage + 1);
  return `${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`;
}

if (inStockCheckbox) {
  inStockCheckbox.addEventListener("change", () => {
    const searchParams = new URLSearchParams(window.location.search);

    if (inStockCheckbox.checked) {
      searchParams.set("filter.v.availability", "1");
    } else {
      searchParams.delete("filter.v.availability");
    }

    searchParams.set("page", "1"); // reset page

    const sectionId = productGrid.dataset.id;
    loader.style.display = "block";

    fetch(`${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        let newProducts = doc.querySelector("#product-grid");
        let newPagination = doc.querySelector("#pagination");

        if (newProducts) {
          productGrid.innerHTML = newProducts.innerHTML;
        }

        if (newPagination) {
          paginationContainer.innerHTML = newPagination.innerHTML;
        } else {
          paginationContainer.innerHTML = ""; // no pagination = 1 page
        }

        // Update DOM references
        productGrid = document.getElementById("product-grid");
        paginationContainer = document.getElementById("pagination");

        // Reset scroll loader state
        currentPage = 1;
        totalPages = getTotalPages();

        // Update URL in browser
        window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);

        initInfiniteLoader();
        loader.style.display = "none";
      })
      .catch(() => loader.style.display = "none");
  });
}

// function initializeScrollAnimationTrigger() {
//   const scrollTriggers = document.querySelectorAll('.scroll-trigger');

//   scrollTriggers.forEach((trigger) => {
//     trigger.classList.add('scroll-trigger--offscreen');

//     const observer = new IntersectionObserver((entries) => {
//       entries.forEach((entry) => {
//         if (entry.isIntersecting) {
//           entry.target.classList.remove('scroll-trigger--offscreen');
//         }
//       });
//     });

//     observer.observe(trigger);
//   });
// }

